import React, { useState } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { AddCircle as AddCircleIcon } from '@mui/icons-material';

import CustomSortableTable, { SortableTableAction, SortableTableFilter, SortableTableHeader, SortableTableRow } from '../../../../components/CustomSortableTable';

import { CategoryItem } from '../Common';

import styles from './styles';
import CustomDialog from '../../../CustomDialog';
import { booleanToYesNo } from '../../../../utils/stringUtils';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
  items: CategoryItem[];
  handleCreate: () => void;
  handleEdit: (id: string, view: string) => void;
  handleActiveStatusChange: (id: string, isActive: boolean) => Promise<void>;
}

const ListSections = ({ classes, history, items, handleCreate, handleEdit, handleActiveStatusChange }: Props): React.ReactElement => {
  const [activeFiltered, setActiveFiltered] = useState<boolean>(true);
  const [recordStatusState, setRecordStatusState] = useState<{
    dialogOpen: boolean;
    categoryId: string;
    isActive: boolean;
    isLoading: boolean;
  }>({
    dialogOpen: false,
    categoryId: '',
    isActive: false,
    isLoading: false,
  });

  const deactivateCategory = async () => {
    setRecordStatusState((prev) => ({ ...prev, isLoading: true }));
    await handleActiveStatusChange(recordStatusState.categoryId, recordStatusState.isActive);
    setRecordStatusState((prev) => ({ ...prev, isLoading: false, dialogOpen: false }));
  };

  const headers: SortableTableHeader[] = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'emergencies', label: 'Emergencies', align: 'center' },
    { key: 'departments', label: 'Departments', align: 'center' },
    { key: 'active', label: 'Active' },
  ];

  const rows: SortableTableRow[] = items.map((item: CategoryItem) => ({
    key: item.id,
    actions: [
      {
        label: item.active ? 'Deactivate' : 'Activate',
        destructive: item.active,
        onClick: () => setRecordStatusState((prev) => ({ ...prev, categoryId: item.id, isActive: !item.active, dialogOpen: true })),
      },
      {
        label: 'Edit',
        onClick: () => handleEdit(item.id, 'grouping'),
      },
    ],
    columns: [
      {
        key: 'name',
        label: item.name,
      },
      {
        key: 'description',
        label: item.description,
      },
      {
        key: 'emergencies',
        label: item.emergency_assignments.length,
      },
      {
        key: 'departments',
        label: item.area_assignments.length,
      },
      {
        key: 'active',
        label: booleanToYesNo(item.active),
      },
    ],
    collapsible: [
      {
        title: 'Emergencies',
        counter: item.emergency_assignments.length,
        headers: [
          { key: 'name', label: 'Name' },
          { key: 'description', label: 'Description' },
        ],
        rows: item.emergency_assignments
          .map((i) => i.emergency)
          .map((i) => ({
            key: i.id,
            actions: [{ label: 'Edit', onClick: () => handleEdit(i.id, 'emergencies') }],
            columns: [
              { key: 'name', label: i.name },
              { key: 'description', label: i.description },
            ],
          })),
      },
      {
        title: 'Departments',
        counter: item.area_assignments.length,
        headers: [
          { key: 'name', label: 'Name' },
          { key: 'phone', label: 'Phone' },
        ],
        rows: item.area_assignments
          .map((i) => i.area)
          .map((i) => ({
            key: i.id,
            actions: [{ label: 'Edit', onClick: () => handleEdit(i.id, 'departments') }],
            columns: [
              { key: 'name', label: i.name },
              { key: 'phone', label: i.phone },
            ],
          })),
      },
    ].filter((i) => i.rows.length > 0),
  }));

  const actions: SortableTableAction[] = [
    {
      key: 'create',
      label: 'Create grouping',
      icon: <AddCircleIcon />,
      onClick: () => handleCreate(),
    },
  ];

  const activeFilter: SortableTableFilter = {
    key: 'activeFilter',
    label: 'Active',
    filter: (rows: SortableTableRow[]) =>
      rows.filter((r) => {
        const col = r.columns.find((c) => c.key === 'active');
        if (col) {
          return activeFiltered ? col.label === 'Yes' : col.label === 'No';
        }
        return true;
      }),
    component: (
      <ToggleButtonGroup className={classes.activeFilter} size="medium" value={[activeFiltered]} color="primary" onChange={() => setActiveFiltered(!activeFiltered)}>
        <ToggleButton className={classes.toggleButton} key="active" value={true}>
          Active
        </ToggleButton>
        <ToggleButton className={classes.toggleButton} key="inactive" value={false}>
          Inactive
        </ToggleButton>
      </ToggleButtonGroup>
    ),
  };

  const filters = [activeFilter];

  const filterRows = (): SortableTableRow[] => {
    let filteredRows: SortableTableRow[] = rows;
    filters.forEach((f) => {
      filteredRows = f.filter(filteredRows);
    });
    return filteredRows;
  };

  return (
    <>
      <Grid container spacing={4} alignItems="stretch">
        <Grid item xs={12}>
          <CustomSortableTable title="Grouping" orderdBy="name" ordered="asc" actions={actions} headers={headers} counter={rows.length} rows={filterRows()} filters={filters} />
        </Grid>
      </Grid>
      <CustomDialog
        open={recordStatusState.dialogOpen}
        title="Update Category"
        message={`Are you sure you want to ${recordStatusState.isActive ? 'activate' : 'deactivate'} this category?`}
        handleClose={() => setRecordStatusState((prev) => ({ ...prev, dialogOpen: false }))}
        actions={[
          { label: 'No', onClick: () => setRecordStatusState((prev) => ({ ...prev, dialogOpen: false })) },
          { label: 'Yes', onClick: () => deactivateCategory() },
        ]}
      />
    </>
  );
};

export default withRouter(withStyles(styles)(ListSections));
